import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

const LocationPickerModal = ({ show, onHide, initialAddress, onConfirm }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [address, setAddress] = useState(initialAddress || '');
  const [location, setLocation] = useState(null);
  
  useEffect(() => {
    if (show) {
      setAddress(initialAddress || '');
    }
  }, [show, initialAddress]);

  useEffect(() => {
    let isMounted = true;
    if (!show) return;

    const initMap = async () => {
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
            console.error("Missing Google Maps API Key");
            return;
        }

        setOptions({
          apiKey: apiKey,
          version: "weekly",
        });

        const { Map } = await importLibrary('maps');
        const { Marker } = await importLibrary('marker');
        const { Geocoder } = await importLibrary('geocoding');

        if (!isMounted) return;

        const defaultLocation = { lat: 20.5937, lng: 78.9629 }; 
        const newMap = new Map(mapRef.current, {
          center: defaultLocation,
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: false,
        });

        setMap(newMap);

        const newMarker = new Marker({
          map: newMap,
          position: defaultLocation,
          draggable: true,
        });
        setMarker(newMarker);
        
        const geocoder = new Geocoder();

        const updateAddressFromLatLng = (latlng) => {
          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === 'OK' && results[0]) {
              if (isMounted) {
                setAddress(results[0].formatted_address);
                setLocation(latlng);
              }
            }
          });
        };

        newMarker.addListener('dragend', () => {
          const pos = newMarker.getPosition();
          const latlng = { lat: pos.lat(), lng: pos.lng() };
          newMap.setCenter(latlng);
          updateAddressFromLatLng(latlng);
        });

        newMap.addListener('click', (e) => {
          const latlng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          newMarker.setPosition(latlng);
          newMap.setCenter(latlng);
          updateAddressFromLatLng(latlng);
        });

        if (initialAddress) {
          geocoder.geocode({ address: initialAddress }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const loc = results[0].geometry.location;
              const latlng = { lat: loc.lat(), lng: loc.lng() };
              newMap.setCenter(latlng);
              newMarker.setPosition(latlng);
              if (isMounted) setLocation(latlng);
            } else {
               // Try geolocation if address fails
               tryGeolocation(newMap, newMarker, updateAddressFromLatLng);
            }
          });
        } else {
            tryGeolocation(newMap, newMarker, updateAddressFromLatLng);
        }
      } catch (err) {
        console.error("Error loading Google Maps in Modal:", err);
      }
    };

    const tryGeolocation = (newMap, newMarker, updateAddressFromLatLng) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                if (!isMounted) return;
                const latlng = { lat: position.coords.latitude, lng: position.coords.longitude };
                newMap.setCenter(latlng);
                newMarker.setPosition(latlng);
                updateAddressFromLatLng(latlng);
            });
        }
    };

    setTimeout(initMap, 200); // Give the modal a tiny bit of time to render

    return () => {
      isMounted = false;
    };
  }, [show]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: '#fff', padding: '20px', borderRadius: '8px',
        width: '90%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '15px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0, color: '#333' }}>Pick Location</h4>
          <button onClick={onHide} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>&times;</button>
        </div>
        
        <div ref={mapRef} style={{ width: '100%', height: '350px', borderRadius: '8px', backgroundColor: '#eee' }}></div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px', color: '#333' }}>Selected Address</label>
          <input 
            type="text" 
            value={address} 
            readOnly 
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', backgroundColor: '#f9f9f9', color: '#333' }}
          />
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
          <button 
            onClick={onHide} 
            style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontWeight: '500' }}
          >
            Cancel
          </button>
          <button 
            onClick={() => onConfirm({ address, location })}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--accent-color, #0d6efd)', color: '#fff', cursor: 'pointer', fontWeight: '500' }}
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LocationPickerModal;
